import { ReactNode } from 'react'
import { PhotoImagePictureGalleryPic } from '@awazevr/design-system.components.icon'
import { SegmentedControl } from '@awazevr/design-system.components.segmented-control'
import type { Image } from '@awazevr/design-system.compositions.image-carousel-overlay'

import { CustomMediaGallery } from '../../CustomMediaGallery'
import { DescriptivePaginationCountContainer } from '../../CustomMediaGallery.styles'
import { DEFAULT_IMAGE_URL } from 'src/constants'

import {
  StyledCarouselContainer,
  StyledCarouselFloatingElements,
  StyledFallbackImage,
  StyledSingleImage,
  StyledSingleImageContainer,
} from './Carousel.styles'

export type CarouselTranslations = {
  imageComingSoon?: string
  previousNavigation?: string
  nextNavigation?: string
  openGalleryTitle?: string
  missingImagesAlt?: string
  singleImageFallbackAlt?: string
}

export type CarouselProps = {
  images?: Image[]
  uniqueId: string
  onCarouselInteractCallback: (
    event: 'open' | 'close' | 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => void
  overlays?: ReactNode
  translations?: CarouselTranslations
}

const DEFAULT_TRANSLATIONS: Required<CarouselTranslations> = {
  imageComingSoon: '',
  previousNavigation: 'Previous',
  nextNavigation: 'Next',
  openGalleryTitle: '',
  missingImagesAlt: 'Property images coming soon',
  singleImageFallbackAlt: 'Property image',
}

export const Carousel = ({
  images,
  uniqueId,
  onCarouselInteractCallback,
  overlays,
  translations,
}: CarouselProps) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }

  let imageContent: ReactNode

  if (!images || images.length === 0) {
    imageContent = (
      <StyledFallbackImage src={DEFAULT_IMAGE_URL} alt={t.missingImagesAlt} />
    )
  } else if (images.length === 1) {
    const [first] = images
    imageContent = (
      <StyledSingleImageContainer>
        <StyledSingleImage
          src={first.src as string}
          alt={first.alt || t.singleImageFallbackAlt}
        />
        <DescriptivePaginationCountContainer shouldDiplayPaginationText>
          <SegmentedControl
            icon={
              <PhotoImagePictureGalleryPic
                iconColour="ColorThemeLink"
                size="small"
              />
            }
            text={`1/1`}
          />
        </DescriptivePaginationCountContainer>
      </StyledSingleImageContainer>
    )
  } else {
    imageContent = (
      <CustomMediaGallery
        onCarouselInteractCallback={onCarouselInteractCallback}
        images={images}
        translations={{
          imageComingSoon: t.imageComingSoon,
          openGalleryTitle: t.openGalleryTitle,
          previousNavigation: t.previousNavigation,
          nextNavigation: t.nextNavigation,
        }}
        uniqueId={uniqueId}
      />
    )
  }

  return (
    <StyledCarouselContainer>
      {overlays && (
        <StyledCarouselFloatingElements>
          {overlays}
        </StyledCarouselFloatingElements>
      )}
      {imageContent}
    </StyledCarouselContainer>
  )
}
